import type { SchemaPathTree } from '@angular/forms/signals';
import { minDateError, required, validate } from '@angular/forms/signals';
import type {
  ProjectReference,
  ProjectReferenceForm,
} from '@indutrust/factory-model';
import { parseIsoDateString } from '@indutrust/shared-util';

const EMPTY_PROJECT_REFERENCE_FORM: ProjectReferenceForm = {
  id: null,
  factory_id: 0,
  description: '',
  customer_name: '',
  country_id: null,
  start_date: '',
  end_date: '',
  industrial_subtype_ids: [],
  capability_ids: [],
  value: '',
  on_demand: false,
};

/**
 * Returns an initial {@link ProjectReferenceForm} value, optionally seeded
 * from an existing {@link ProjectReference}.
 *
 * Replaces the previous `createProjectReferenceFormGroup` factory.
 */
export function createProjectReferenceFormValue(
  projectReference?: Partial<ProjectReference>
): ProjectReferenceForm {
  if (!projectReference) {
    return { ...EMPTY_PROJECT_REFERENCE_FORM };
  }

  return {
    ...EMPTY_PROJECT_REFERENCE_FORM,
    ...projectReference,
    id: projectReference.id ?? null,
    industrial_subtype_ids: projectReference.industrial_subtype_ids ?? [],
    capability_ids: projectReference.capability_ids ?? [],
    value:
      projectReference.value === undefined
        ? ''
        : String(projectReference.value),
  };
}

/**
 * Schema fragment applying the project-reference form's validation rules.
 *
 * End date must not precede start date — emitted as a `minDate` error on
 * `end_date` (via {@link minDateError}) so `getError('minDate')` works.
 *
 * @example
 * const projectReferenceForm = form(model, projectReferenceFormSchema);
 */
export function projectReferenceFormSchema(
  p: SchemaPathTree<ProjectReferenceForm>
): void {
  required(p.factory_id);
  required(p.description);
  required(p.customer_name);
  required(p.start_date);
  required(p.end_date);
  required(p.industrial_subtype_ids);
  required(p.value);

  validate(p.end_date, (ctx) => {
    const startDate = parseIsoDateString(ctx.valueOf(p.start_date));
    const endDate = parseIsoDateString(ctx.value());
    if (!startDate || !endDate || endDate >= startDate) {
      return null;
    }
    return minDateError(startDate);
  });
}
